'use client';

import { useMemo, useState } from 'react';
import { Title } from '@/components/title';
import { TInvoiceData } from '@/types/invoice';
import { useTranslations } from '@/hooks/useTranslations';
import { TableWrapper } from '@/components/table/table-wrapper';
import { useInvoiceContext } from '@/contexts/invoice-context';
import { defineInvoiceColumns } from '@/components/table/columns/invoice/invoice-columns';
import { ExtendedUser } from '@/types/next-auth';
import { FileText, Upload } from 'lucide-react';
import { BulkInvoiceActions } from '@/components/table/columns/bulk-invoice-actions';
import { Button } from '@/components/ui/button';
import { CsvExportButton } from '@/components/csv-export-button';
import { exportInvoicesCSV } from '@/actions';
export const InvoicesDashboard = ({
  data,
  user,
}: {
  data: TInvoiceData[];
  user: ExtendedUser;
}) => {
  const { dispatch } = useInvoiceContext();
  const [recurringFilter, setRecurringFilter] = useState<
    'all' | 'recurring' | 'one-time'
  >('all');

  const t = useTranslations('InvoicesDashboard');
  const translation = useTranslations('InvoiceTable');
  const common = useTranslations('Common');

  const filteredData = useMemo(() => {
    if (recurringFilter === 'all') return data;
    if (recurringFilter === 'recurring')
      return data.filter((d) => d.isRecurring);
    return data.filter((d) => !d.isRecurring);
  }, [data, recurringFilter]);

  const columns = defineInvoiceColumns(useTranslations('InvoiceTable'), user);

  const extraFilters = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => dispatch({ type: 'OPEN_IMPORT_INVOICE_POPUP' })}
        aria-label="Import invoices"
      >
        <Upload className="h-4 w-4 mr-2" />
        {t('importInvoice')}
      </Button>
      <CsvExportButton
        label={common('exportCsv')}
        exportingLabel={common('exportingCsv')}
        errorLabel={common('exportCsvError')}
        onExport={() => exportInvoicesCSV()}
      />
      <select
        value={recurringFilter}
        onChange={(e) =>
          setRecurringFilter(e.target.value as 'all' | 'recurring' | 'one-time')
        }
        className="flex h-9 w-[140px] lg:w-[160px] rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <option value="all">{translation('filterRecurring')}</option>
        <option value="recurring">{translation('filterRecurringOnly')}</option>
        <option value="one-time">{translation('filterOneTimeOnly')}</option>
      </select>
    </>
  );

  return (
    <div className="p-3 md:p-10">
      <Title translation={t} />

      <TableWrapper<TInvoiceData>
        columns={columns}
        data={filteredData}
        filters={true}
        extraFilters={extraFilters}
        pagination={true}
        count={20}
        isFull={true}
        translation={translation}
        searchLabel="client"
        cta={{
          label: t('createInvoice'),
          onClick: () =>
            dispatch({
              type: 'OPEN_CREATE_INVOICE_POPUP',
            }),
        }}
        emptyState={{
          title: useTranslations('Empty')('invoices.title'),
          description: useTranslations('Empty')('invoices.description'),
          icon: <FileText className="h-12 w-12" />,
          hideLearnMore: true,
        }}
        bulkActions={(table) => <BulkInvoiceActions table={table} />}
        selectedCountLabel={translation('invoicesSelected')}
      />
    </div>
  );
};
